import React, {useMemo} from "react";
import {Grid, List, ListItem, Stack, styled, Typography} from "@mui/material";
import {THEME_CONFIG} from "../../../config";
import {getTranslatedAnswers} from "../../../functions/getTranslatedAnswers";
import useLanguageStore from "../../../store/languageStore";

const {poll, pollBorder, pollTextColor, borderRadius} = THEME_CONFIG

export default function PhotoPollQuestion({risposte, risposteTranslations, hideResult = false, answer, sendAnswer, isPoll}) {
    const { currentLanguage, defaultLanguage } = useLanguageStore();

    // Ottieni le risposte tradotte
    const translatedRisposte = useMemo(() => {
        if (!risposte || !Array.isArray(risposte) || risposte.length === 0) {
            return risposte || [];
        }

        const translated = getTranslatedAnswers(risposte, risposteTranslations, currentLanguage, defaultLanguage);

        if (!Array.isArray(translated)) {
            return risposte;
        }

        // Per PhotoPollQuestion, le risposte possono essere oggetti o stringhe
        // Se abbiamo oggetti, mantieni la struttura originale ma aggiorna solo il label
        // Se risposte è array di oggetti
        if (typeof risposte[0] === 'object' && risposte[0] !== null) {
            // Gestisci ogni elemento individualmente per supportare array misti
            return risposte.map((original, index) => {
                // Assicurati che original sia un oggetto valido
                if (!original || typeof original !== 'object') {
                    return original;
                }

                const translatedItem = translated[index];

                // Se la traduzione è una stringa, usa solo per aggiornare il label
                if (typeof translatedItem === 'string') {
                    return {
                        ...original,
                        label: translatedItem
                    };
                }

                // Se la traduzione è un oggetto, mergia mantenendo tutte le proprietà dell'originale
                if (translatedItem && typeof translatedItem === 'object' && translatedItem !== null) {
                    return {
                        ...original,
                        label: translatedItem.label !== undefined && translatedItem.label !== null
                            ? String(translatedItem.label)
                            : original.label
                    };
                }

                // Se non c'è traduzione o è null/undefined, usa l'originale
                return original;
            });
        }

        // Se risposte è array di stringhe, restituisci translated direttamente
        // Assicurati che tutti gli elementi siano stringhe o valori validi
        return translated.map((item, index) => {
            return item !== undefined && item !== null ? item : risposte[index];
        });
    }, [risposte, risposteTranslations, currentLanguage, defaultLanguage]);

    // Trova l'indice della risposta data (answer) nelle risposte originali
    const answerIndex = useMemo(() => {
        if (!answer || answer === '') return -1;
        return risposte.findIndex(r => {
            const originalLabel = typeof r === 'object' ? r.label : r;
            return originalLabel === answer;
        });
    }, [answer, risposte]);
    const RispostaStyled = styled(ListItem)({
        border: `2px solid ${pollBorder.default}`,
        borderRadius: borderRadius.default,
        background: poll.default,
        margin: '1rem 0',
        cursor: 'pointer',
        color: pollTextColor.default,
        padding: 8,
    })

    const computeBkg = (index, isCorrect) => {
        if(!isPoll && hideResult === false) {
            if (answerIndex !== -1) {
                if (isCorrect) {
                    return poll.correct
                } else if (answerIndex === index && !isCorrect){
                    return poll.wrong
                }
            }
        } else {
            if(answerIndex === index) {
                return poll.answered
            }
        }

    }

    const computeBorderColor = (index, isCorrect) => {
        if(!isPoll && hideResult === false) {
            if (answerIndex !== -1) {
                if (isCorrect) {
                    return pollBorder.correct
                } else if (answerIndex === index && !isCorrect){
                    return pollBorder.wrong
                }
            }
        } else {
            if(answerIndex === index) {
                return pollBorder.answered
            }
        }
    }

    const computeTextColor = (index, isCorrect) => {
        if(!isPoll && hideResult === false) {
            if (answerIndex !== -1) {
                if (isCorrect) {
                    return pollTextColor.correct
                } else if (answerIndex === index && !isCorrect) {
                    return pollTextColor.wrong
                }
            }
        } else {
            if(answerIndex === index) {
                return pollTextColor.answered
            }
        }
    }

    const Risposte = () => (
        translatedRisposte.map((r, i) => {
            // Assicurati che r sia sempre un oggetto o una stringa valida
            if (!r || (typeof r !== 'object' && typeof r !== 'string') || (typeof r === 'object' && !r)) {
                return null;
            }

            const isPicturesPoll = typeof r === 'object' && r !== null ? !!r?.image?.url : false;
            const isCorrect = typeof r === 'object' && r !== null ? r?.isCorrect : false;
            // Estrai il testo in modo sicuro: sempre una stringa
            let rispText = "";
            if (typeof r === 'object' && r !== null) {
                // Se è un oggetto, estrai il label
                const label = r?.label;
                rispText = (typeof label === 'string' ? label : String(label || "")) || "";
            } else if (typeof r === 'string') {
                rispText = r;
            } else {
                // Fallback: converti tutto in stringa
                rispText = String(r || "");
            }

            // Assicurati che rispText sia sempre una stringa prima del rendering
            if (typeof rispText !== 'string') {
                rispText = String(rispText || "");
            }
            return <Grid key={i} item xs={isPicturesPoll ? 6 : 12}
                  sx={{
                      scale: isPicturesPoll ? answerIndex !== -1 ? answerIndex === i ? '1.05' : '0.9' : '1' : 'inherit',
                      opacity: (isCorrect && !hideResult) ? '1' : isPicturesPoll ? answerIndex !== -1 ? answerIndex === i ? '1' : '0.5' : '1' : 'inherit',
                  }}>
            <RispostaStyled key={i} style={{
                width: '100%',
                background: computeBkg(i, isCorrect),
                border: computeBorderColor(i, isCorrect) ? '2px solid '+ computeBorderColor(i, isCorrect) : `2px solid ${pollBorder.default}`,
                color: computeTextColor(i, isCorrect) ?  computeTextColor(i, isCorrect) :  pollTextColor.default,
                margin: isPicturesPoll ? 'inherit' : '1rem 0',
            }} onClick={() => rispondi(i)}>
                <Stack>
                    {
                        isPicturesPoll &&
                            <img
                                src={r?.image?.url}
                                alt={rispText}
                                style={{
                                    aspectRatio: '1/1',
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: borderRadius.default,
                                }}/>
                    }
                    {rispText && <Typography sx={{mt: isPicturesPoll ? 1 : 'inherit'}} textAlign={"center"} fontWeight={'bolder'}
                                 variant={isPicturesPoll ? 'body2' : 'h6'}>
                        {rispText}
                    </Typography>}
                </Stack>
            </RispostaStyled>
            </Grid>
        })
    )

    const rispondi = (index) => {
        // Usa l'indice per ottenere la risposta originale nella lingua di default
        const originalRisposta = risposte[index];
        const originalAnswer = typeof originalRisposta === 'object' ? originalRisposta.label : originalRisposta;

        if (isPoll) {
            sendAnswer(originalAnswer)
        } else {
            if (answer === '') {
                sendAnswer(originalAnswer)
            }
        }
    }

    const isPicturesPoll = translatedRisposte && translatedRisposte[0] && !!translatedRisposte[0]?.image?.url

    return (
        <List>
            {isPicturesPoll ?
                <Grid container spacing={1} justifyContent={'center'}>
                    <Risposte/>
                </Grid>
            : <Risposte/>}
        </List>
    )
}
